// src/components/widgets/PulsoDevolucionesWidget.js
// Widget "Pulso de Devoluciones" — la tasa de devolución del último mes
// analizado vs el promedio histórico, con semáforo, dinero perdido, canal
// y tendencia mensual. Lee los docs YA calculados del módulo Análisis de
// Devoluciones (`analisis_devoluciones/{YYYY-MM}`, cohorte por mes de ENVÍO,
// excluye las 4 redes internas — ver CALCULOS.md): 6 lecturas por apertura,
// sin cron nuevo. El mes EN CURSO no se muestra a propósito: sus devoluciones
// todavía están llegando (censura por derecha) y daría una tasa falsa de baja.
// Se muestra por PERMISO (`widget-devoluciones`, sección WIDGETS de Permisos).
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { ArrowUturnLeftIcon, ChartBarIcon, TruckIcon, ExclamationTriangleIcon } from '../ui/Icons';
import useEsMovil from '../../hooks/useEsMovil';
import useConteoAnimado from '../../hooks/useConteoAnimado';
import { ESTILO_TILE, BadgeTendencia } from './widgetsUI';

const estilosResponsivos = `
  @media (max-width: 768px) {
    .devs-widget-container { margin: 15px 0 !important; }
    .devs-header {
      flex-direction: column !important;
      align-items: center !important;
      text-align: center;
      margin-bottom: 12px !important;
    }
    .devs-header h3 { font-size: 18px !important; margin: 0 !important; }
    .devs-header-der { width: 100%; flex-direction: column !important; gap: 8px !important; }
    .btn-abrir-devs { width: 100% !important; }
    .devs-tiles { grid-template-columns: 1fr 1fr !important; gap: 8px !important; }
    .devs-tile { padding: 10px !important; }
    .devs-viz-grid { grid-template-columns: 1fr !important; gap: 10px !important; }
    .devs-card { padding: 12px !important; }
  }
`;

const NOMBRES_MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MES_CORTO = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const formatearMoneda = (monto) => {
  return 'Q ' + parseFloat(monto).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

// 'YYYY-MM' del mes cerrado más reciente y los N anteriores (zona GT)
const mesesCerrados = (cantidad) => {
  const hoy = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Guatemala' }).format(new Date());
  let [anio, mes] = hoy.split('-').map(Number);
  const lista = [];
  for (let i = 0; i < cantidad; i++) {
    mes -= 1;
    if (mes === 0) { mes = 12; anio -= 1; }
    lista.unshift(`${anio}-${String(mes).padStart(2, '0')}`); // cronológico asc
  }
  return lista;
};

const nombreMes = (clave, corto = false) => {
  const [anio, mes] = clave.split('-').map(Number);
  return corto ? MES_CORTO[mes - 1] : `${NOMBRES_MES[mes - 1]} ${anio}`;
};

// conEnlace: el botón "Abrir Análisis" sale solo si la persona puede abrir Análisis de Devoluciones (candado de ruta de
// Layout.js, lo decide Inicio): el permiso del widget no da el módulo.
function PulsoDevolucionesWidget({ conEnlace = false }) {
  const [meses, setMeses] = useState(null); // [{clave, data}] solo los que existen, asc
  const [mesCerradoFalta, setMesCerradoFalta] = useState(false);
  const [cargando, setCargando] = useState(true);
  const isMobile = useEsMovil();

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const claves = mesesCerrados(6); // últimos 6 meses cerrados
        const snaps = await Promise.all(claves.map((c) => getDoc(doc(db, 'analisis_devoluciones', c))));
        if (!vivo) return;
        const existentes = [];
        snaps.forEach((s, i) => { if (s.exists()) existentes.push({ clave: claves[i], data: s.data() }); });
        setMeses(existentes);
        setMesCerradoFalta(!snaps[snaps.length - 1].exists()); // el mes cerrado más reciente no está calculado
      } catch (e) {
        console.error('Error cargando el pulso de devoluciones:', e);
        if (vivo) setMeses([]);
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  // Último mes analizado + promedio histórico (hasta 4 meses anteriores a ese)
  const ultimo = meses && meses.length > 0 ? meses[meses.length - 1] : null;
  const global = ultimo?.data?.porCanal?.Global || null;
  const historicos = meses && meses.length > 1 ? meses.slice(0, -1).slice(-4) : [];
  const promedioHistorico = historicos.length > 0
    ? historicos.reduce((s, m) => s + (m.data?.porCanal?.Global?.porcentajeDev || 0), 0) / historicos.length
    : null;
  const tendenciaVsHistorico = global && promedioHistorico > 0
    ? ((global.porcentajeDev - promedioHistorico) / promedioHistorico) * 100
    : null;

  // Conteos animados (los hooks van antes de cualquier return)
  const tasaAnimada = useConteoAnimado(global?.porcentajeDev || 0);
  const devsAnimadas = useConteoAnimado(global?.devoluciones || 0);
  const perdidoAnimado = useConteoAnimado(global?.qPerdidos || 0);
  const lagAnimado = useConteoAnimado(global?.lagPromedio || 0);

  // Barras que se llenan al aparecer los datos
  const [barrasListas, setBarrasListas] = useState(false);
  useEffect(() => {
    if (!meses || meses.length === 0) return;
    setBarrasListas(false);
    const t = setTimeout(() => setBarrasListas(true), 60);
    return () => clearTimeout(t);
  }, [meses]);

  if (cargando) {
    return (
      <>
        <style>{estilosResponsivos}</style>
        <div className="devs-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: isMobile ? '12px' : '16px' }}>
            <div className="skeleton" style={{ width: '260px', height: '24px', borderRadius: 'var(--radio-sm)' }} />
            <div className="skeleton" style={{ width: '280px', height: '34px', borderRadius: 'var(--radio-sm)' }} />
          </div>
          <div className="devs-tiles" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: isMobile ? '8px' : '14px', marginBottom: '14px' }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="skeleton" style={{ height: '84px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
          <div className="devs-viz-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))', gap: isMobile ? '10px' : '14px' }}>
            {[0, 1].map((i) => (
              <div key={i} className="skeleton" style={{ height: '160px', borderRadius: 'var(--radio-lg)' }} />
            ))}
          </div>
        </div>
      </>
    );
  }

  if (!meses) return null;

  const canales = ultimo ? ['Forza', 'Capital'].map((c) => ({
    canal: c,
    ...(ultimo.data?.porCanal?.[c] || {})
  })) : [];
  const maxTasaCanal = Math.max(...canales.map((c) => c.porcentajeDev || 0), 1);
  const maxTasaMes = Math.max(...meses.map((m) => m.data?.porCanal?.Global?.porcentajeDev || 0), 1);
  const COLOR_CANAL = { Forza: 'var(--color-info)', Capital: 'var(--color-primario)' };

  return (
    <>
      <style>{estilosResponsivos}</style>
      <div className="devs-widget-container" style={{ maxWidth: '1200px', margin: isMobile ? '15px 0' : '30px auto' }}>
        {/* Header estilo dashboard */}
        <div className="devs-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          <h3 style={{ margin: 0, fontSize: isMobile ? '18px' : '22px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ color: 'var(--color-primario-fuerte)', display: 'inline-flex' }}><ArrowUturnLeftIcon className="w-6 h-6" /></span>
            Pulso de Devoluciones
          </h3>
          <div className="devs-header-der" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            {ultimo && (
              <small
                title="El mes en curso no se muestra: sus devoluciones todavía están llegando y daría una tasa engañosamente baja"
                style={{ color: 'var(--color-texto-secundario)', fontSize: '12px' }}
              >
                Último mes analizado: {nombreMes(ultimo.clave)}
              </small>
            )}
            {conEnlace && (
            <Link
              to="/analisis-devoluciones"
              className="btn-abrir-devs btn-press hover-bright"
              style={{
                height: '38px',
                boxSizing: 'border-box',
                padding: '0 20px',
                backgroundColor: 'var(--color-primario)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radio-sm)',
                cursor: 'pointer',
                fontSize: '14px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                textDecoration: 'none'
              }}
            >
              <ChartBarIcon className="w-4 h-4" /> Abrir Análisis
            </Link>
            )}
          </div>
        </div>

        {!ultimo ? (
          <div className="card devs-card" style={{ ...ESTILO_TILE, fontSize: '13px', color: 'var(--color-texto-secundario)' }}>
            Todavía no hay meses calculados. Entrá a <strong>Análisis de Devoluciones</strong> y usá "Calcular este mes" para armar el histórico.
          </div>
        ) : (
          <>
            {/* Aviso: el mes cerrado más reciente aún no se calculó */}
            {mesCerradoFalta && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                marginBottom: isMobile ? '10px' : '14px',
                backgroundColor: 'var(--color-advertencia-fondo)',
                border: '1px solid var(--color-advertencia)',
                borderRadius: 'var(--radio-sm)',
                fontSize: '12px',
                color: 'var(--color-advertencia-texto)',
                textAlign: 'left'
              }}>
                <span style={{ display: 'inline-flex', flexShrink: 0 }}><ExclamationTriangleIcon className="w-4 h-4" /></span>
                El análisis de {nombreMes(mesesCerrados(1)[0])} todavía no se calculó — generalo en Análisis de Devoluciones para tener el dato más fresco.
              </div>
            )}

            {/* Fila de KPI tiles */}
            <div className="devs-tiles stagger-list" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))',
              gap: isMobile ? '8px' : '14px',
              marginBottom: isMobile ? '10px' : '14px'
            }}>
              <div className="card devs-tile" style={ESTILO_TILE} title={`Devoluciones ÷ pedidos no anulados enviados en ${nombreMes(ultimo.clave)} (cohorte por mes de envío, sin redes internas)`}>
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Tasa de devolución</div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                    {tasaAnimada.toFixed(1)}%
                  </span>
                  <BadgeTendencia
                    pct={tendenciaVsHistorico}
                    invertir
                    etiqueta="vs histórico"
                    textoIgual="igual al histórico"
                    titulo={promedioHistorico != null ? `Comparada contra el promedio de los ${historicos.length} meses anteriores (${promedioHistorico.toFixed(1)}%) — acá subir es malo` : ''}
                  />
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  {promedioHistorico != null ? `promedio histórico: ${promedioHistorico.toFixed(1)}%` : nombreMes(ultimo.clave)}
                </div>
              </div>

              <div className="card devs-tile" style={ESTILO_TILE} title={`Pedidos que terminaron en devolución de los enviados en ${nombreMes(ultimo.clave)}`}>
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Devoluciones</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                  {Math.round(devsAnimadas).toLocaleString('en-US')}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  de {(global.noAnulados || 0).toLocaleString('en-US')} pedidos enviados
                </div>
              </div>

              <div className="card devs-tile" style={ESTILO_TILE} title="Dinero que se fue por las devoluciones del mes (cargos de envío y producto perdido)">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Dinero perdido</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-peligro-texto)', fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                  {formatearMoneda(perdidoAnimado)}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  por devoluciones de {nombreMes(ultimo.clave, true)}
                </div>
              </div>

              <div className="card devs-tile" style={ESTILO_TILE} title="Días promedio entre el envío del pedido y su devolución">
                <div style={{ fontSize: '12px', color: 'var(--color-texto-secundario)', marginBottom: '4px' }}>Lag promedio</div>
                <div style={{ fontSize: '22px', fontWeight: 'bold', color: 'var(--color-texto)', fontVariantNumeric: 'tabular-nums' }}>
                  {global.lagPromedio != null ? `${lagAnimado.toFixed(1)} días` : '—'}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '2px' }}>
                  del envío a la devolución
                </div>
              </div>
            </div>

            {/* Visualizaciones: por canal + tendencia mensual */}
            <div className="devs-viz-grid stagger-list" style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, minmax(0, 1fr))',
              gap: isMobile ? '10px' : '14px'
            }}>
              {/* Por canal */}
              <div className="card devs-card" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex' }}><TruckIcon className="w-4 h-4" /></span> Por canal · {nombreMes(ultimo.clave, true)}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1, justifyContent: 'center' }}>
                  {canales.map((c) => (
                    <div key={c.canal}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6px', fontSize: '12px', marginBottom: '3px' }}>
                        <span>
                          <strong>{c.canal}</strong>{' '}
                          <span style={{ color: 'var(--color-texto-secundario)' }}>
                            {(c.devoluciones || 0).toLocaleString('en-US')} de {(c.noAnulados || 0).toLocaleString('en-US')} pedidos
                          </span>
                        </span>
                        <strong style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{(c.porcentajeDev || 0).toFixed(1)}%</strong>
                      </div>
                      <div style={{ width: '100%', height: '8px', backgroundColor: 'var(--color-borde)', borderRadius: 'var(--radio-sm)', overflow: 'hidden' }}>
                        <div style={{
                          width: barrasListas ? `${((c.porcentajeDev || 0) / maxTasaCanal) * 100}%` : '0%',
                          height: '100%',
                          backgroundColor: COLOR_CANAL[c.canal],
                          transition: 'width 0.8s ease'
                        }} />
                      </div>
                    </div>
                  ))}
                  <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)' }}>
                    Dinero perdido: <strong style={{ color: 'var(--color-peligro-texto)' }}>{formatearMoneda(canales.reduce((s, c) => s + (c.qPerdidos || 0), 0))}</strong>
                    {' '}({canales.map((c) => `${c.canal} ${formatearMoneda(c.qPerdidos || 0)}`).join(' · ')})
                  </div>
                </div>
              </div>

              {/* Tendencia mensual */}
              <div className="card devs-card" style={{ ...ESTILO_TILE, display: 'flex', flexDirection: 'column' }}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--color-texto-secundario)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <span style={{ display: 'inline-flex' }}><ChartBarIcon className="w-4 h-4" /></span> Tendencia de la tasa (meses cerrados)
                </div>
                <div style={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'stretch' }}>
                  {meses.map((m) => {
                    const tasa = m.data?.porCanal?.Global?.porcentajeDev || 0;
                    const esUltimo = m.clave === ultimo.clave;
                    return (
                      <div key={m.clave} style={{ flex: 1, minWidth: 0, textAlign: 'center', display: 'flex', flexDirection: 'column' }} title={`${nombreMes(m.clave)}: ${tasa.toFixed(2)}% (${m.data?.porCanal?.Global?.devoluciones || 0} devoluciones)`}>
                        <div style={{ fontSize: '10px', fontVariantNumeric: 'tabular-nums', color: esUltimo ? 'var(--color-primario-texto)' : 'var(--color-texto-secundario)', fontWeight: esUltimo ? 'bold' : 'normal' }}>
                          {tasa.toFixed(1)}%
                        </div>
                        <div style={{ flex: 1, minHeight: '70px', display: 'flex', alignItems: 'flex-end', marginTop: '2px' }}>
                          <div style={{
                            width: '100%',
                            height: `${Math.max((tasa / maxTasaMes) * 100, tasa > 0 ? 3 : 1.5)}%`,
                            backgroundColor: esUltimo ? 'var(--color-primario)' : 'var(--color-borde)',
                            borderRadius: '3px 3px 0 0',
                            transform: barrasListas ? 'scaleY(1)' : 'scaleY(0)',
                            transformOrigin: 'bottom',
                            transition: 'transform 0.6s ease'
                          }} />
                        </div>
                        <div style={{
                          fontSize: '10px',
                          marginTop: '3px',
                          color: esUltimo ? 'var(--color-primario-texto)' : 'var(--color-texto-secundario)',
                          fontWeight: esUltimo ? 'bold' : 'normal'
                        }}>
                          {nombreMes(m.clave, true)}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--color-texto-secundario)', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--color-borde-suave)' }}>
                  El mes en curso no aparece: sus devoluciones todavía están en camino.
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default PulsoDevolucionesWidget;
